import { lazy, Suspense, useEffect, useState, useMemo } from 'react';
import { MapContainer } from 'react-leaflet';
import { RedundantTileLayers } from './components/map/RedundantTileLayers';
import { useNetworkElementsStore } from './store/networkElementsStore';
import { useNetworkMapStore } from './store/networkMapStore';
import { useSettingsStore } from './store/settingsStore';
import { useThemeStore } from './store/themeStore';
import { useTabStore, isCrossZoneTab, ZONELESS_TAB_TYPES, TAB_SCOPES, type TabType } from './store/tabStore';
import { useZoneStore } from './store/zoneStore';
import { networkUpdatesService } from './services/networkUpdatesService';
import { initLoadFlowPolling, cleanupLoadFlowPolling } from './store/loadFlowStore';
import { TabBar } from './components/TabBar';
import { Sidebar } from './components/Sidebar';
import { ControlSidebar } from './components/ControlSidebar';
import { StressTestSidebar } from './components/stress-sidebar/StressTestSidebar';
import { StatusOverlay } from './components/StatusOverlay';
import { MapPositionHandler, MapClickHandler } from './components/map/MapPositionHandlers';
import NetworkElements from './components/map/NetworkElements';
import { TrennstellenLayer } from './components/map/TrennstellenLayer';
import { ZonePolygonLayer } from './components/map/ZonePolygonLayer';
import { ZoomDisplay } from './components/map/ZoomDisplay';
import { FitZoneButton } from './components/map/FitZoneButton';
import { MapViewHandler } from './components/map/MapViewHandler';
import { MapOverlay } from './components/map/MapOverlay';
import { NetworkStateWidget } from './components/map/NetworkStateWidget';
import { HighZoomHandler } from './components/map/HighZoomHandler';
import LoginPanel from './components/auth/LoginPanel';
import { authService } from './services/authService';
import { useGlobalAlerts } from './hooks/useGlobalAlerts';
import LandingPage from './pages/LandingPage';
import { useFeatureStore, useCapabilities } from './store/featureStore';
import { isTabAvailable } from './features/capabilities';
import { useHelpStore } from './help/helpStore';
import { ImportChangesDialog } from './components/import/ImportChangesDialog';
import { Toaster } from 'sonner';
import { ConnectionStatusBanner } from './components/ui/ConnectionStatusBanner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './components/map/leaflet-fixes.css';
import './components/map/map-performance.css';

// Secondary pages are code-split: the dashboard and the map are what every session opens,
// the rest (monitoring charts, stress scenarios, settings, Netzanschluss, docs) loads on
// first use. Keeps chart.js & co. out of the initial bundle.
const NetworkGridPage = lazy(() => import('./components/grid/NetworkGridPage').then(m => ({ default: m.NetworkGridPage })));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const SystemDependenciesPage = lazy(() => import('./components/documentation/SystemDependenciesPage').then(m => ({ default: m.SystemDependenciesPage })));
const HelpPanel = lazy(() => import('./components/help/HelpPanel').then(m => ({ default: m.HelpPanel })));
const StressSzenariosPage = lazy(() => import('./pages/stress-scenarios'));
const MonitoringPage = lazy(() => import('./pages/MonitoringPage'));
const SwitchingHistoryPage = lazy(() => import('./pages/SwitchingHistoryPage'));
const NetzanschlussPage = lazy(() => import('./pages/NetzanschlussPage'));
const NetworkMonitoringPage = lazy(() => import('./pages/NetworkMonitoringPage'));

const PageFallback = () => (
  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400 text-sm">Lade Ansicht …</div>
);

// Fix Leaflet's default icon paths
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function App() {
  // Authentication state
  const [authState, setAuthState] = useState(authService.getAuthState());
  const [devMode, setDevMode] = useState(false);
  const [authCheckDone, setAuthCheckDone] = useState(false);
  const isAuthenticated = useMemo(() => authState.isAuthenticated || devMode, [authState, devMode]);

  // Check if auth is disabled (dev mode) on startup
  useEffect(() => {
    // Clear reloading flag (set by BaseApiClient on 401)
    sessionStorage.removeItem('auth_reloading');

    authService.checkAuthStatus().then(status => {
      if (status.devMode) {
        setDevMode(true);
      }
      setAuthCheckDone(true);
    });

    // Optional-module discovery (anonymous endpoint) — gates module UI.
    void useFeatureStore.getState().loadFeatures();
  }, []);

  // Store hooks - Always initialize these regardless of auth state.
  // Field selectors only: a whole-store subscription here re-rendered the entire app
  // shell (tab bar, sidebars, every map layer incl. 147 zone polygons) on every write
  // to the element store, i.e. on every 10 s power update of a 1000-element zone.
  const loadElements = useNetworkElementsStore(state => state.loadElements);
  const setLoading = useNetworkElementsStore(state => state.setLoading);
  const setError = useNetworkElementsStore(state => state.setError);
  const pendingImportChanges = useNetworkElementsStore(state => state.pendingImportChanges);
  const setPendingImportChanges = useNetworkElementsStore(state => state.setPendingImportChanges);
  const applyPendingChanges = useNetworkElementsStore(state => state.applyPendingChanges);
  const discardPendingChanges = useNetworkElementsStore(state => state.discardPendingChanges);
  const mapPosition = useNetworkMapStore(state => state.mapPosition);
  const loadSettings = useSettingsStore(state => state.loadSettings);
  const loadZones = useZoneStore(state => state.loadZones);
  const activeZoneId = useZoneStore(state => state.activeZoneId);
  const zonesInitialized = useZoneStore(state => state.initialized);
  const theme = useThemeStore(state => state.theme);
  const activeTab = useTabStore(state => state.tabs.find(tab => tab.id === state.activeTabId));
  const capabilities = useCapabilities();
  const activeTabType = activeTab && isTabAvailable(activeTab.type, capabilities) ? activeTab.type : 'landing';
  const helpPanelOpen = useHelpStore(s => s.panelOpen);
  const isZoneless = zonesInitialized && !activeZoneId;

  useEffect(() => {
    const openHelpLink = (event?: PopStateEvent) => {
      if (new URL(window.location.href).searchParams.has('help')) {
        useHelpStore.getState().closePanel();
        useTabStore.getState().openTab('documentation');
      } else if (event && typeof event.state?.gridpilotTab === 'string' && event.state.gridpilotTab in TAB_SCOPES) {
        useTabStore.getState().openTab(event.state.gridpilotTab as TabType);
      }
    };
    openHelpLink();
    window.addEventListener('popstate', openHelpLink);
    return () => window.removeEventListener('popstate', openHelpLink);
  }, []);

  // Global SSE alert listener — fires toasts on any page, zone-independent
  useGlobalAlerts();

  // Apply theme class to document root
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Initialize app data when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    const initialize = async () => {
      setLoading(true);
      setError(false, ''); // Clear previous errors

      try {

        // CRITICAL: Load zones FIRST
        await loadZones();

        // Check if a zone is selected (from persisted state or previous session)
        const zoneId = useZoneStore.getState().activeZoneId;

        if (!zoneId) {
          // ZONELESS STATE - NO FALLBACK
          // User must explicitly select a zone before we load any network data
          setLoading(false);
          return; // Don't load network data without a zone
        }

        // Zone is selected - load zone-scoped data

        await loadSettings();

        await loadElements();

        // Connect to SSE for real-time updates (AFTER data loaded)
        // NOTE: SSE is used for element-update/delete/create events, NOT load flow results
        networkUpdatesService.connect(zoneId);

        // Initialize load flow REST polling (10s interval, replaces SSE for load flow)
        initLoadFlowPolling();

        // Start power flow service AFTER zone data is loaded
        await import('./services/powerFlowService');
      } catch (error) {
        console.error('[App] Error during initialization:', error);
        setError(true, error instanceof Error ? error.message : 'Failed to load network data');
      } finally {
        setLoading(false);
      }
    };

    // Start initialization
    initialize().catch(error => {
      console.error('[App] Unhandled initialization error:', error);
      setError(true, error instanceof Error ? error.message : 'Failed to initialize application');
      setLoading(false);
    });

    // Cleanup function - disconnect SSE and stop load flow polling when unmounting
    return () => {
      networkUpdatesService.disconnect();
      cleanupLoadFlowPolling();
    };
  }, [isAuthenticated, loadElements, loadSettings, loadZones, setError, setLoading]);

  // Wait for auth status check to complete
  if (!authCheckDone) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Lade...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPanel onLoginSuccess={() => setAuthState(authService.getAuthState())} />;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden relative">
      <ConnectionStatusBanner />
      <Toaster richColors position="top-right" theme={theme} />
      {/* Top Bar - full width */}
      <TabBar showLogout={authState.isAuthenticated && !devMode} />

      {/* Below: ControlSidebar + Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - swaps based on active tab (hidden on the cross-zone pages) */}
        {!isCrossZoneTab(activeTabType) && (
          <div className={`z-20 h-full ${activeTabType === 'documentation' ? 'hidden lg:block' : ''}`}>
            {activeTabType === 'stress' ? <StressTestSidebar /> : <ControlSidebar />}
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0 relative z-10 flex flex-col">
        <StatusOverlay />

        {/* Zoneless State Overlay - shown when no zone is selected (not on pages that work without one) */}
        {isZoneless && !ZONELESS_TAB_TYPES.has(activeTabType) && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-gray-900/50 backdrop-blur-xs">
            <div className="bg-white dark:bg-primary-800 rounded-lg shadow-xl p-8 max-w-md mx-4 text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-accent-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-primary-100 mb-2">
                Keine Zone ausgewählt
              </h2>
              <p className="text-gray-600 dark:text-primary-300 mb-6">
                Bitte wählen Sie eine Zone aus dem Dropdown-Menü in der linken Seitenleiste, um Netzwerkdaten anzuzeigen.
              </p>
              <div className="flex items-center justify-center text-sm text-gray-500 dark:text-primary-400">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
                <span>Zone-Auswahl links in der Sidebar</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 relative overflow-y-auto">
          {/* Landing page */}
          {activeTabType === 'landing' && <LandingPage />}

          {/* Map is ALWAYS rendered, only hidden when not active (for performance) */}
          <div className={activeTabType === 'map' ? 'h-full' : 'hidden'}>
            <MapContainer
              center={mapPosition.center}
              zoom={mapPosition.zoom}
              minZoom={8}
              maxZoom={22}
              className="w-full h-full"
              zoomControl={true}
              zoomDelta={0.5}
              zoomSnap={0.5}
              wheelDebounceTime={50}
              preferCanvas={true}
              fadeAnimation={false}
              markerZoomAnimation={true}
            >
              <RedundantTileLayers />
              <ZonePolygonLayer />
              <MapPositionHandler />
              <MapClickHandler />
              <MapViewHandler />
              <HighZoomHandler />
              <NetworkElements />
              <TrennstellenLayer />
              <MapOverlay />
              <NetworkStateWidget />
              <FitZoneButton />
              <ZoomDisplay />
            </MapContainer>
          </div>

          {/* Other views are conditionally rendered (code-split, see the lazy imports above) */}
          <Suspense fallback={<PageFallback />}>
            {activeTabType === 'network-monitoring' && <NetworkMonitoringPage />}
            {activeTabType === 'grid' && <NetworkGridPage />}
            {activeTabType === 'settings' && <SettingsPage />}
            {activeTabType === 'documentation' && <SystemDependenciesPage />}
            {activeTabType === 'stress' && <StressSzenariosPage />}
            {activeTabType === 'monitoring' && <MonitoringPage />}
            {activeTabType === 'switching-history' && <SwitchingHistoryPage />}
            {activeTabType === 'netzanschluss' && <NetzanschlussPage />}
          </Suspense>
        </div>
      </div>

      {/* Right Details Sidebar - Overlay on map view */}
      {/* pointer-events-none: the wrapper spans the full sidebar width even
          when the panel is collapsed (translated off-screen) — without this it
          silently swallows clicks on the map strip and banner buttons beneath.
          The Sidebar re-enables pointer events on its visible parts. */}
      {activeTabType === 'map' && (
        <div className={`absolute right-0 top-0 h-screen z-20 pointer-events-none ${helpPanelOpen ? 'invisible' : ''}`}>
          <Sidebar />
        </div>
      )}

      {/* Import Changes Dialog - shown after network import if there are pending changes */}
      {pendingImportChanges && (
        <ImportChangesDialog
          pendingChanges={pendingImportChanges}
          onApply={applyPendingChanges}
          onDiscard={discardPendingChanges}
          onClose={() => setPendingImportChanges(null)}
        />
      )}
      </div>{/* end flex row wrapper */}
      {helpPanelOpen && activeTabType !== 'documentation' && <Suspense fallback={null}><HelpPanel /></Suspense>}
    </div>
  );
}

export default App;
